import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import {
  formatPriceValue,
  isPriceFilterField,
  parseActiveRange,
  RANGE_INDICATOR
} from 'libs/athos/components/collection/filters/FilterUtils'
import debounce from 'lodash.debounce'
import { formatValue } from 'libs/athos/components/collection/filters/FilterHooks'

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

/**
 * Read a usable `[low, high]` pair off a facet. Athos returns `range: [0, 0]`
 * for a slider facet whose backing field carries no data, and may omit `range`
 * entirely — both are treated as "no range".
 * @param {Object} facet - Facet object from the API
 * @returns {[number, number]|null}
 */
const readRange = (facet) => {
  const low = Number(facet?.range?.[0])
  const high = Number(facet?.range?.[1])
  if (!Number.isFinite(low) || !Number.isFinite(high) || high <= low) return null
  return [low, high]
}

/**
 * Resolve the slider bounds from the live facet, falling back to the snapshot
 * captured on first load (`initialFilters`) so the track doesn't collapse onto
 * the current selection once a range is applied and the live facet narrows.
 * Returns null when neither carries a real range, which is the caller's signal
 * to hide the filter rather than render a dead "$0 / $0" control.
 * @param {Object} filter - Facet snapshot
 * @param {Object} fetchedFilter - Live facet for the current result set
 * @returns {{ min: number, max: number }|null}
 */
export const getSliderBounds = (filter, fetchedFilter) => {
  const ranges = [readRange(fetchedFilter), readRange(filter)].filter(Boolean)
  if (!ranges.length) return null

  return {
    min: Math.min(...ranges.map(([low]) => low)),
    max: Math.max(...ranges.map(([, high]) => high))
  }
}

export function FilterInputSlider({ filter, onFilter, onRemove, fetchedFilter, activeFilters }) {
  const bounds = getSliderBounds(filter, fetchedFilter)

  // No usable bounds: hide the filter instead of rendering an inert slider.
  if (!bounds) return null

  // Floor the step at 1 so price moves in whole units. AMC sends a fractional
  // step (0.25) for price, which would otherwise make the track unusably fine.
  const step = Math.max(filter.step ?? 1, 1)
  const activeRange = parseActiveRange(filter.field, activeFilters)

  const handleChange = (value) => {
    // Fully open means "no constraint": drop any applied range rather than
    // emitting one that merely restates the bounds.
    if (value.min <= bounds.min && value.max >= bounds.max) {
      if (activeRange) {
        onRemove?.(`${filter.field}:${activeRange.min}${RANGE_INDICATOR}${activeRange.max}`)
      }
      return
    }

    onFilter(`${filter.field}:${value.min}${RANGE_INDICATOR}${value.max}`)
  }

  return (
    <div class="flex w-full flex-col">
      <RangeSlider
        min={bounds.min}
        max={bounds.max}
        step={step}
        format={filter.format}
        onChange={handleChange}
        activeRange={activeRange}
        field={filter.field}
      />
    </div>
  )
}

const RangeSlider = ({ min, max, step, onChange, format, activeRange, field }) => {
  const [minValue, setMinValue] = useState(clamp(activeRange?.min ?? min, min, max))
  const [maxValue, setMaxValue] = useState(clamp(activeRange?.max ?? max, min, max))

  // Keep the thumbs in step with the applied filter: hydrates from a shared or
  // reloaded URL, and snaps back to the bounds when the range is cleared
  // elsewhere (a pill, "Clear all").
  useEffect(() => {
    setMinValue(clamp(activeRange?.min ?? min, min, max))
    setMaxValue(clamp(activeRange?.max ?? max, min, max))
  }, [activeRange?.min, activeRange?.max, min, max])

  // One debounced refine per drag. The latest `onChange` is held in a ref so the
  // debounced function can be built once — rebuilding it per render would cancel
  // the pending call on every input event.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const debouncedOnChange = useMemo(() => debounce((values) => onChangeRef.current(values), 300), [])

  useEffect(() => {
    return () => debouncedOnChange.cancel()
  }, [debouncedOnChange])

  const handleMinChange = (event) => {
    const newMinVal = Math.min(+event.target.value, maxValue - step)
    setMinValue(newMinVal)
    debouncedOnChange({ min: newMinVal, max: maxValue })
  }

  const handleMaxChange = (event) => {
    const newMaxVal = Math.max(+event.target.value, minValue + step)
    setMaxValue(newMaxVal)
    debouncedOnChange({ min: minValue, max: newMaxVal })
  }

  const minPos = ((minValue - min) / (max - min)) * 100
  const maxPos = ((maxValue - min) / (max - min)) * 100
  const thumbStyle =
    'absolute top-1/2 w-[--thumb-size] h-[--thumb-size] bg-brand-black rounded-full -translate-x-1/2 -translate-y-1/2 z-2'
  const labelStyle = 'text-s'
  const inputStyle = 'range absolute size-full p-0 opacity-0 appearance-none pointer-events-none z-3'

  const formatLabelValue = (value) => {
    if (isPriceFilterField(field)) {
      return formatPriceValue(value)
    }
    return format ? formatValue(value, format) : value
  }

  return (
    <div className="flex w-full flex-col gap-y-1.5">
      <div className="flex items-center justify-between">
        <p className={labelStyle}>
          Min <span className="text-type-black-secondary">{formatLabelValue(minValue)}</span>
        </p>
        <p className={labelStyle}>
          Max <span className="text-type-black-secondary">{formatLabelValue(maxValue)}</span>
        </p>
      </div>
      <div className="px-1.25 md:px-0.625">
        <div className="relative mx-[calc(var(--thumb-size)_/_-2)] flex h-[--thumb-size] items-center">
          <div className="absolute mx-[calc(var(--thumb-size)_/_-2)] h-[--thumb-size] w-[calc(100%_+_var(--thumb-size))]">
            <input
              className={inputStyle}
              type="range"
              value={minValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMinChange}
              aria-label="Min"
            />
            <input
              className={inputStyle}
              type="range"
              value={maxValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMaxChange}
              aria-label="Max"
            />
          </div>
          <div className="absolute h-[--thumb-size] w-full">
            <div className={thumbStyle} style={{ left: `${minPos}%` }} />
            <div className="absolute top-1/2 z-0 h-0.125 w-full -translate-y-1/2 bg-page-secondary">
              <div
                className="absolute h-full bg-brand-black"
                style={{ left: `${minPos}%`, right: `${100 - maxPos}%` }}
              />
            </div>
            <div className={thumbStyle} style={{ left: `${maxPos}%` }} />
          </div>
        </div>
      </div>
    </div>
  )
}
